import { useEffect, useRef } from 'react';
import { trackEvent } from '@/components/GoogleAnalytics';
import { trackEvent as trackEventHj } from '@/components/Hotjar';
import { trackEventMp } from '@/components/Mixpanel';

interface ScrollTrackingOptions {
  thresholds?: number[];
  onScrollDepth?: (depth: number) => void;
}

export const useScrollTracking = (options: ScrollTrackingOptions = {}) => {
  const { thresholds = [25, 50, 75, 90, 100], onScrollDepth } = options;
  const trackedDepths = useRef<Set<number>>(new Set());
  const isTrackingRef = useRef(false);

  useEffect(() => {
    if (isTrackingRef.current) return;
    isTrackingRef.current = true;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollPercentage = Math.round((scrollTop / documentHeight) * 100);

      // Track each threshold only once
      thresholds.forEach(threshold => {
        if (scrollPercentage >= threshold && !trackedDepths.current.has(threshold)) {
          trackedDepths.current.add(threshold);
          
          // Track in Google Analytics
          trackEvent('scroll_depth', {
            event_category: 'engagement',
            event_label: `scroll_${threshold}_percent`,
            scroll_depth: threshold,
            page_path: window.location.pathname
          });

          // Track in Mixpanel
          trackEventMp('scroll_depth', {
            event_category: 'engagement',
            event_label: `scroll_${threshold}_percent`,
            scroll_depth: threshold,
            page_path: window.location.pathname
          });

          // Track in Hotjar
          trackEventHj(`scroll_depth_${threshold}`);

          // Custom callback
          onScrollDepth?.(threshold);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      isTrackingRef.current = false;
    };
  }, [thresholds, onScrollDepth]);

  // Reset tracking for new page
  const resetTracking = () => {
    trackedDepths.current.clear();
  };

  return { resetTracking };
};

// Hook for tracking time spent on page
export const useTimeTracking = () => {
  const startTimeRef = useRef<number>(Date.now());
  const hasTrackedRef = useRef<boolean>(false);

  useEffect(() => {
    const trackTimeSpent = () => {
      if (hasTrackedRef.current) return;
      
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      
      // Track significant time milestones
      if (timeSpent >= 30) {
        trackEvent('time_on_page', {
          event_category: 'engagement',
          event_label: 'time_30_seconds',
          time_spent: timeSpent
        });
        trackEventMp('time_on_page', {
          event_category: 'engagement',
          event_label: 'time_30_seconds',
          time_spent: timeSpent
        });
        trackEventHj('time_on_page_30s');
        hasTrackedRef.current = true;
      }
    };

    // Track on page visibility change (user leaving)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackTimeSpent();
      }
    };

    // Track on beforeunload
    const handleBeforeUnload = () => {
      trackTimeSpent();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Also track after 30 seconds
    const timer = setTimeout(trackTimeSpent, 30000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearTimeout(timer);
    };
  }, []);
};